// This is a base class for all FormClasses (e.g. EnrolmentForm, PaymentAuthorizationForm)
// It has a abstract signature getFormMappings() which is implemented by every form class, which returns a FoxItFields array with correct field name and value
// It contains logic for gathering commonly used plan data, such as initial, full names, address, funeral home details.
import type { GeoAddress, Vendor } from '@/apiTypes'
import Plan from '@/classes/Plan'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import { useSessionStore } from '@/stores/session'
import { DateTime } from 'luxon'

export abstract class FormMapping {
	protected planHolder: Plan
	private sessionStore = useSessionStore()
	private authStore = useAuthStore()

	protected constructor(planHolderId: string) {
		this.planHolder = this.sessionStore.getPlanHolder(planHolderId) as Plan
		this.sessionStore = useSessionStore()
	}

	// Helper to return a checkbox value given the condition
	protected getCheckBoxMapping(name: string, condition: boolean) {
		return {
			name,
			value: condition ? 'Yes' : 'Off',
		}
	}

	// Simple helper to format numbers with 2 decimal places and comma separators
	protected formatAmount(value: number): string {
		if (value === 0) return ''
		return new Intl.NumberFormat('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			useGrouping: true,
		}).format(value)
	}

	// Helper for generating the middleInitials for policyOwner and proposed Insured (used in Enrolment)
	protected getMiddleNameInitialMapping() {
		return [
			{
				name: 'insuredMI',
				value: this.planHolder.middleName?.slice(0, 1) ?? '',
			},
			{
				name: 'purchaserMI',
				// policyOwner keeps its data when "owner is insured" is checked, so ignore it then.
				value: this.planHolder.ownerIsInsured
					? ''
					: (this.planHolder.policyOwner?.middleName?.slice(0, 1) ?? ''),
			},
		]
	}

	// Helper to calculate the age of the proposedInsured (date considered, used in enrolment)
	protected calculateProposedInsuredAge(): string | undefined {
		if (!this.planHolder.birthDate) return undefined

		const birthDate = DateTime.fromISO(this.planHolder.birthDate)
		const today = DateTime.now()

		return Math.floor(today.diff(birthDate, 'years').years).toString()
	}

	// Helper to gather personal information of the proposedInsured
	protected getProposedInsuredPersonalInfoMapping() {
		return [
			{
				name: 'insuredGender',
				value: this.planHolder.gender ?? '',
			},
			{
				name: 'insuredDateofBirth',
				value: this.planHolder.birthDate ?? '',
			},
		]
	}

	// Helper to return Proposed Insured personal data fields
	protected getProposedInsuredMapping() {
		const fullName = this.planHolder.middleName
			? `${this.planHolder.firstName} ${this.planHolder.middleName} ${this.planHolder.lastName}`
			: `${this.planHolder.firstName} ${this.planHolder.lastName}`

		return [
			{
				name: 'insuredFullName',
				value: fullName,
			},
			{
				name: 'insuredFirstName',
				value: this.planHolder.firstName,
			},
			{
				name: 'insuredMiddleName',
				value: this.planHolder.middleName,
			},
			{
				name: 'insuredLastName',
				value: this.planHolder.lastName,
			},
			this.getCheckBoxMapping(
				'insuredMaleCheck',
				this.planHolder.gender === 'male'
			),
			this.getCheckBoxMapping(
				'insuredFemaleCheck',
				this.planHolder.gender === 'female'
			),
			{
				name: 'insuredDateofBirth',
				value: this.planHolder.birthDate
					? DateTime.fromISO(this.planHolder.birthDate).toFormat('M/d/yyyy')
					: undefined,
			},
			{
				name: 'purchaserEmail',
				value: this.planHolder.email ?? '',
			},
			{
				name: 'insuredAge',
				value: this.calculateProposedInsuredAge(),
			},
			{
				name: 'insuredPhone',
				value: this.planHolder.phone,
			},
			{
				name: 'insuredSSN',
				value: this.planHolder.socialSecurityNumber,
			},
			{
				name: 'beneficiaryOtherThanFuneralFirm',
				value: this.planHolder.beneficiary,
			},
			{
				name: 'beneficiaryRelationship',
				value: this.planHolder.beneficiaryRelationship,
			},
		]
	}

	// Helper for gathering the address details for the proposed Insured
	protected getProposedInsuredAddressMapping() {
		const insuredFullAddress = [
			this.planHolder.address,
			this.planHolder.city,
			this.planHolder.state,
		].join(', ')
		return [
			{
				name: 'insuredFullAddress',
				value: insuredFullAddress,
			},
			{
				name: 'insuredAddress',
				value: this.planHolder.address ?? '',
			},
			{
				name: 'insuredCity',
				value: this.planHolder.city ?? '',
			},
			{
				name: 'insuredState',
				value: this.planHolder.state ?? '',
			},
			{
				name: 'insuredZip',
				value: this.planHolder.zip ?? '',
			},
		]
	}

	// Helper to return Policy Owner (purchaser) personal data fields
	protected getPolicyOwnerMapping() {
		const purchaser = this.planHolder.ownerIsInsured
			? this.planHolder
			: this.planHolder.policyOwner

		const fullName = purchaser.middleName
			? `${purchaser.firstName} ${purchaser.middleName} ${purchaser.lastName}`
			: `${purchaser.firstName} ${purchaser.lastName}`

		return [
			{
				name: 'purchaserFullName',
				value: fullName,
			},
			{
				name: 'purchaserFirstName',
				value: purchaser.firstName ?? '',
			},
			{
				name: 'purchaserLastName',
				value: purchaser.lastName ?? '',
			},
			{
				name: 'purchaserSSN',
				value: purchaser.socialSecurityNumber ?? '',
			},
			{
				name: 'purchaserEmail',
				value: purchaser.email ?? '',
			},
			{
				name: 'purchaserPhone',
				value: purchaser.phone ?? '',
			},
			{
				name: 'purchaserSecondaryPhoneNumber',
				value: purchaser.secondaryPhone ?? '',
			},
		]
	}

	protected getPolicyOwnerAddressMapping() {
		let purchaserInfo
		if (this.planHolder.ownerIsInsured) {
			purchaserInfo = {
				address: this.planHolder.address,
				city: this.planHolder.city,
				state: this.planHolder.state,
				zip: this.planHolder.zip,
				phone: this.planHolder.phone,
			}
		} else {
			purchaserInfo = {
				address: this.planHolder.policyOwner.address ?? '',
				city: this.planHolder.policyOwner.city ?? '',
				state: this.planHolder.policyOwner.state ?? '',
				zip: this.planHolder.policyOwner.zip ?? '',
				phone: this.planHolder.policyOwner.phone ?? '',
			}
		}

		const purchaserFullAddress = [
			purchaserInfo.address,
			purchaserInfo.city,
			purchaserInfo.state,
		].join(', ')

		const purchaserCityAndState =
			purchaserInfo.city && purchaserInfo.state
				? `${purchaserInfo.city}, ${purchaserInfo.state}`
				: ''

		return [
			{
				name: 'purchaserFullAddress',
				value: purchaserFullAddress,
			},
			{
				name: 'purchaserAddress',
				value: purchaserInfo.address,
			},
			{
				name: 'purchaserCity',
				value: purchaserInfo.city,
			},
			{
				name: 'purchaserState',
				value: purchaserInfo.state,
			},
			{
				name: 'purchaserCityAndState',
				value: purchaserCityAndState,
			},
			{
				name: 'purchaserZip',
				value: purchaserInfo.zip,
			},
			{
				name: 'purchaserPhone',
				value: purchaserInfo.phone,
			},
		]
	}

	// Helper to gather funeral home information
	protected getFuneralHomeMapping() {
		const crmStore = useCrmStore()

		const funeralHome = crmStore.findFuneralHome(this.planHolder.funeralHomeId)

		if (!funeralHome) return []

		const funeralHomeAddress = funeralHome.billingAddress as GeoAddress
		const funeralHomeCityState = [
			funeralHomeAddress.city,
			funeralHomeAddress.state,
		].join(', ')

		const funeralHomeFullAddress = [
			funeralHomeAddress.street,
			funeralHomeCityState,
		].join(', ')

		const funeralHomeCityStateZip = [
			funeralHomeCityState,
			funeralHomeAddress.postalCode,
		].join(', ')

		return [
			{
				name: 'funeralHomeName',
				value: funeralHome?.name ?? '',
			},
			{
				name: 'funeralHomeFullAddress',
				value: funeralHomeFullAddress ?? '',
			},
			{
				name: 'funeralHomeAddress',
				value: funeralHomeAddress.street ?? '',
			},
			{
				name: 'funeralHomeCity',
				value: funeralHomeAddress.city ?? '',
			},
			{
				name: 'funeralHomeState',
				value: funeralHomeAddress.stateCode ?? '',
			},
			{
				name: 'funeralHomeCityState',
				value: funeralHomeCityState,
			},
			{
				name: 'funeralHomeCityAndState',
				value: funeralHomeCityState,
			},
			{
				name: 'funeralHomeCityStateZip',
				value: funeralHomeCityStateZip,
			},
			{
				name: 'funeralHomeZip',
				value: funeralHomeAddress.postalCode ?? '',
			},
			{
				name: 'funeralHomePhone',
				value: funeralHome.phoneNumber ?? '',
			},
		]
	}

	protected getSalesContactName(): string {
		const salesContact = this.authStore.user?.salesContact

		return (
			salesContact?.legalName ??
			salesContact?.name ??
			`${salesContact?.firstname ?? ''} ${salesContact?.lastname ?? ''}`
		).trim()
	}

	// Helper to fill in funeral home representative fields
	protected getFuneralHomeRepresentativeMappings() {
		const insuranceProducerNumber = this.planHolder.insuranceProducerNumber

		return [
			{
				name: 'funeralHomeRepresentativePrintedName',
				value: this.getSalesContactName(),
			},
			{
				name: 'funeralHomeRepresentativeProducerNumber',
				value: insuranceProducerNumber?.producerNumber ?? '',
			},
		]
	}

	// Helper for supplemental forms like Preneed Price Contract and Revocable Form to fill in signature dates
	protected getRepresentativeSignatureDateMappings() {
		const date = DateTime.now()
		return [
			{
				name: 'funeralHomeRepresentativeSignatureDay',
				value: date.day,
			},
			{
				name: 'funeralHomeRepresentativeSignatureMonth',
				value: date.monthShort,
			},
			{
				name: 'funeralHomeRepresentativeSignatureLastTwoDigitsYear',
				value: date.year.toString().slice(2),
			},
		]
	}

	abstract getFormMappings(): Array<{
		name: string
		value: string | number | undefined
	}>
}
